import { EventEmitter, Logger } from '@nova/utils';
import { Assets } from 'pixi.js';

import { CameraManager } from '../camera/CameraManager';
import { GameManager } from '../game/GameManager';

import { ESceneState } from './ESceneState';
import { IScene, ISceneConstructor } from './IScene';

export type ISceneLifecycleAction = 'start' | 'stop' | 'pause' | 'resume' | 'transition';

export interface ISceneLifecycleEvent {
  action: ISceneLifecycleAction;
  sceneID: string;
  activeSceneIDs: string[];
  pausedSceneIDs: string[];
}

export type SceneLifecycleListener = (event: ISceneLifecycleEvent) => void;

export class SceneManager {
  /** Empty until scenes are registered; safe to query before `initialize()` (e.g. early extension bootstrap). */
  private static _sceneMap = new Map<string, IScene>();
  private static _activeScenes = new Map<string, IScene>();
  private static _pausedScenes = new Map<string, IScene>();
  private static _defaultLoadingScene: IScene;
  private static _baseActiveScene: string;
  private static _activeLoadingScene?: IScene;
  private static _onSceneLifecycleChange: EventEmitter<[ISceneLifecycleEvent]> = new EventEmitter();

  public static get baseSceneName(): string {
    return this._baseActiveScene;
  }

  public static get baseScene(): IScene | undefined {
    return this.get(this._baseActiveScene);
  }

  /**
   * Fires after each start, stop, pause, resume, or transition. Payload lists reflect scene state **after** the action.
   * Prefer pairing subscribe/unsubscribe; {@link destroy} clears all listeners.
   */
  public static onSceneLifecycleChange(listener: SceneLifecycleListener): void {
    this._onSceneLifecycleChange.add(listener);
  }

  /**
   * Unsubscribe a listener registered with {@link onSceneLifecycleChange}.
   * All lifecycle listeners are removed when {@link destroy} runs.
   */
  public static offSceneLifecycleChange(listener: SceneLifecycleListener): void {
    this._onSceneLifecycleChange.remove(listener);
  }

  private static emitSceneLifecycleChange(action: ISceneLifecycleAction, sceneID: string): void {
    this._onSceneLifecycleChange.emit({
      action,
      sceneID,
      activeSceneIDs: Array.from(this._activeScenes.keys()),
      pausedSceneIDs: Array.from(this._pausedScenes.keys())
    });
  }

  /**
   * Initializes the scene manager.
   * Sets up update ticker and resize event
   *
   * @param defaultLoadingSceneConstructor - An optional default loading scene to be displayed when starting a new scene
   */
  public static async initialize(defaultLoadingSceneConstructor?: ISceneConstructor): Promise<void> {
    this._sceneMap.clear();
    this._activeScenes.clear();
    this._pausedScenes.clear();

    if (defaultLoadingSceneConstructor) {
      this._defaultLoadingScene = await this.initializeLoadingScene(defaultLoadingSceneConstructor, '');
    }

    GameManager.app.ticker.add(this.update.bind(this));
  }

  /**
   * Initializes the loading scene by creating an instance of the specified loading scene constructor,
   * calling the `init`, `preload`, and `start` methods in the appropriate order, and returning the loading scene.
   *
   * @param loadingSceneConstructor - The constructor function for the loading scene
   * @param sceneID - The scene that is currently being loaded
   * @returns A promise that resolves to the initialized loading scene.
   */
  private static async initializeLoadingScene(loadingSceneConstructor: ISceneConstructor, sceneID: string): Promise<IScene> {
    const loadingScene = new loadingSceneConstructor();
    loadingScene.init?.(sceneID);
    await loadingScene.preload?.();
    await Assets.loadBundle(loadingScene.assetBundles);
    loadingScene.start?.();
    return loadingScene;
  }

  public static get(sceneID: string): IScene | undefined {
    return this._sceneMap.get(sceneID);
  }

  /**
   * Instantiates and adds a scene to the scene map
   *
   * @param sceneConstructor - The scene to add
   */
  public static add(sceneConstructor: ISceneConstructor): void {
    const sceneID: string = sceneConstructor.sceneID;
    if (!this._sceneMap.get(sceneID)) {
      const newScene: IScene = new sceneConstructor();
      this._sceneMap.set(sceneID, newScene);
      newScene.sceneState = ESceneState.INITIALIZED;
    }
    else {
      Logger.info(`Scene with ID ${sceneID} already exists.`);
    }
  }

  /**
   * Removes the scene from the scene map and destroys it.
   *
   * @param sceneID - The ID of the scene to remove
   */
  public static remove(sceneID: string): void {
    const sceneToRemove = this._sceneMap.get(sceneID);
    if (sceneToRemove) {
      sceneToRemove.sceneState = ESceneState.DESTROYED;
      if (this._activeScenes.delete(sceneID)) {
        this.removeScene(sceneToRemove);
        sceneToRemove.destroy();
      }
      this._sceneMap.delete(sceneID);
    }
  }

  /**
   * Performs a full scene transition by stopping all running scenes and starting a new one
   *
   * @param sceneID - The ID of the scene to start and switch to
   * @param data - Data to pass to the scene's init method
   * @param displayLoad - Toggle whether to display a loading screen when starting this scene
   */
  public static async transitionToScene(sceneID: string, data?: unknown, displayLoad = true): Promise<void> {
    const sceneToSwitch = this._sceneMap.get(sceneID);
    if (!sceneToSwitch) {
      Logger.error(`Scene with ID ${sceneID} does not exist.`);
      return;
    }

    const sceneState: ESceneState = sceneToSwitch.sceneState;
    if (sceneState === ESceneState.INITIALIZED || sceneState === ESceneState.STOPPED) {
      if (new Map([...this._activeScenes, ...this._pausedScenes]).size > 0) {
        this.stopAll();
      }
      CameraManager.reset();
      this._baseActiveScene = sceneID;
      await this.start(sceneID, data, displayLoad);
      this.emitSceneLifecycleChange('transition', sceneID);
    }
  }

  public static isSceneRunning(sceneID: string): boolean {
    return this._activeScenes.has(sceneID);
  }

  public static isScenePaused(sceneID: string): boolean {
    return this._pausedScenes.has(sceneID);
  }

  /**
   * Starts a scene and adds it to the stage.
   * The start process involves calling init on the scene, followed by loading its assets and finally calling start.
   *
   * @param sceneID - ID of the scene to add
   * @param data - Data to pass to the scene's init method
   * @param displayLoad - Toggle whether to display a loading screen when starting this scene
   * @returns The scene that was started
   */
  public static async start(sceneID: string, data?: unknown, displayLoad = false): Promise<IScene | undefined> {
    const sceneToStart = this._sceneMap.get(sceneID);
    if (!sceneToStart) {
      throw new Error(`Scene with ID ${sceneID} does not exist.`);
    }

    const sceneState: ESceneState = sceneToStart.sceneState;
    if (sceneState === ESceneState.INITIALIZED || sceneState === ESceneState.STOPPED) {
      sceneToStart.sceneState = ESceneState.STARTING;
      sceneToStart.init?.(data);

      sceneToStart.sceneState = ESceneState.LOADING;
      try {
        if (displayLoad && (sceneToStart.loadingScene || this._defaultLoadingScene)) {
          const loadingScene = (sceneToStart.loadingScene
            && await SceneManager.initializeLoadingScene(sceneToStart.loadingScene, sceneID)) || this._defaultLoadingScene;
          this.attachScene(loadingScene);
          this._activeLoadingScene = loadingScene;
          await Assets.loadBundle(sceneToStart.assetBundles, loadingScene.downloadProgress?.bind(loadingScene));
          await sceneToStart.preload?.();
          this._activeLoadingScene = undefined;
          this.removeScene(loadingScene);
          loadingScene.stop?.();
        }
        else {
          await Assets.loadBundle(sceneToStart.assetBundles, undefined);
          await sceneToStart.preload?.();
        }
      }
      catch (error) {
        Logger.error(`Error starting scene ${sceneID}:`, error);
        throw error;
      }

      sceneToStart.sceneState = ESceneState.LOADED;
      sceneToStart.start?.();

      this.attachScene(sceneToStart);

      this._activeScenes.set(sceneID, sceneToStart);
      sceneToStart.sceneState = ESceneState.RUNNING;
      this.emitSceneLifecycleChange('start', sceneID);

      return sceneToStart;
    }

    Logger.warn(`Scene with ID ${sceneID} is already running.`);
    return sceneToStart;
  }

  private static attachScene(scene: IScene): void {
    if (scene.attachToCamera) {
      CameraManager.camera.addChild(scene);
    }
    else {
      GameManager.app.stage.addChild(scene);
    }
  }

  private static removeScene(scene: IScene): void {
    if (scene.attachToCamera) {
      CameraManager.camera.removeChild(scene);
    }
    else {
      GameManager.app.stage.removeChild(scene);
    }
  }

  /**
   * Stops all currently running scenes
   */
  public static stopAll() {
    const currentScenes = new Map([...this._activeScenes, ...this._pausedScenes]);
    for (const key of currentScenes.keys()) {
      this.stop(key);
    }
  }

  /**
   * Stops a scene and removes it from the stage.
   *
   * @param sceneID - The ID of the scene to stop
   */
  public static stop(sceneID: string): void {
    const sceneToStop = this._sceneMap.get(sceneID);
    if (sceneToStop) {
      const sceneState: ESceneState = sceneToStop.sceneState;
      if (sceneState === ESceneState.RUNNING || sceneState === ESceneState.PAUSED) {
        sceneToStop.sceneState = ESceneState.STOPPED;
        sceneToStop.stop?.();

        this.removeScene(sceneToStop);
        this._activeScenes.delete(sceneID);
        this.emitSceneLifecycleChange('stop', sceneID);
      }
    }
  }

  /**
   * Pauses a scene, thus it will remain on the stage, but not receive any updates.
   *
   * @param sceneID - ID of the scene to pause
   */
  public static pause(sceneID: string): void {
    const sceneToPause = this._sceneMap.get(sceneID);
    if (sceneToPause) {
      const sceneState: ESceneState = sceneToPause.sceneState;
      if (sceneState === ESceneState.RUNNING) {
        sceneToPause.sceneState = ESceneState.PAUSED;
        sceneToPause.pause?.();
        this._activeScenes.delete(sceneID);
        this._pausedScenes.set(sceneID, sceneToPause);
        this.emitSceneLifecycleChange('pause', sceneID);
      }
    }
  }

  /**
   * Resumes a scene if it was paused. The scene will now receive updates
   *
   * @param sceneID - ID of the scene to resume
   */
  public static resume(sceneID: string, data?: unknown): void {
    const sceneToResume = this._sceneMap.get(sceneID);
    if (sceneToResume) {
      const sceneState: ESceneState = sceneToResume.sceneState;
      if (sceneState === ESceneState.PAUSED) {
        sceneToResume.resume?.(data);
        this._activeScenes.set(sceneID, sceneToResume);
        this._pausedScenes.delete(sceneID);
        sceneToResume.sceneState = ESceneState.RUNNING;
        this.emitSceneLifecycleChange('resume', sceneID);
      }
    }
  }

  /**
   * The main update loop for any active scenes.
   */
  private static update(): void {
    if (this._activeLoadingScene) {
      this._activeLoadingScene.update?.(GameManager.app.ticker.elapsedMS);
    }

    for (const scene of this._activeScenes.values()) {
      if (scene.update && scene.sceneState === ESceneState.RUNNING) {
        scene.update(GameManager.app.ticker.elapsedMS);
      }
    }
  }

  /**
   * The resize event callback for any active scenes.
   */
  public static resize(): void {
    for (const scene of SceneManager._activeScenes.values()) {
      if (scene.resize && scene.sceneState === ESceneState.RUNNING) {
        scene.resize(GameManager.screenWidth, GameManager.screenHeight, GameManager.worldWidth, GameManager.worldHeight);
      }
    }
  }

  /**
   * Destroys the SceneManager and all scenes.
   */
  public static destroy(): void {
    // Remove ticker update
    if (GameManager.app?.ticker) {
      GameManager.app.ticker.remove(this.update.bind(this));
    }

    // Destroy all scenes in the scene map
    for (const [, scene] of this._sceneMap) {
      scene.sceneState = ESceneState.DESTROYED;
      scene.destroy();
    }

    // Clear all maps
    this._sceneMap.clear();
    this._activeScenes.clear();
    this._pausedScenes.clear();
    this._onSceneLifecycleChange.removeAll();
    this._defaultLoadingScene = undefined as never;
    this._activeLoadingScene = undefined;
    this._baseActiveScene = undefined as never;
  }
}
